import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutocompleteDropdown,
  Box,
  Controller,
  ControllerWrapper,
  FieldTypes,
  FieldWrapper,
  FormFieldsContainer,
  Label,
  Option,
  RegularField,
  Text,
  ToggleButtonGroup,
  useFormContext,
  useResponsive,
} from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import { ICreateDocumentAggregatorDtoDocumentTypeEnum } from '@/dtos';
import {
  DESCRIPTION_MAX_LENGTH,
  LettersOfMedicalNecessityForm,
  LettersOfMedicalNecessityFormField,
} from '@/modules/MyDocuments/hooks/useLettersOfMedicalNecessityForm';
import { LMNPopupMode } from '@/modules/MyDocuments/myDocuments.types';

const TYPE_OPTIONS: Option[] = [
  {
    key: ICreateDocumentAggregatorDtoDocumentTypeEnum.LETTER_OF_MEDICAL_NECESSITY,
    title: 'Letter of Medical Necessity',
  },
];

interface Props {
  mode: LMNPopupMode;
  createForClaimId?: string;
}

const DocumentPreviewPopupForm: React.FC<Props> = ({ mode, createForClaimId }) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext<LettersOfMedicalNecessityForm>();
  const { isMobile } = useResponsive();

  return (
    <Box
      pad={isMobile ? 'spacing16' : { vertical: 'spacing24', horizontal: 'spacing32' }}
      background="canvas"
      round="container1Round"
    >
      <FormFieldsContainer>
        {createForClaimId ? null : (
          <FieldWrapper>
            <Label
              name={LettersOfMedicalNecessityFormField.type}
              isNarrow
              showRequireIcon={mode === LMNPopupMode.UPLOAD}
            >
              {t('Type')}
            </Label>
            <ControllerWrapper>
              <Controller
                name={LettersOfMedicalNecessityFormField.type}
                control={control}
                render={({ field }) =>
                  mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT ? (
                    <Text>
                      {TYPE_OPTIONS?.find((value) => value?.key === field.value)?.title || EMPTY_FIELD_VALUE}
                    </Text>
                  ) : isMobile ? (
                    <AutocompleteDropdown
                      testId="lmn_type"
                      name={LettersOfMedicalNecessityFormField.type}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('Type')}
                      options={TYPE_OPTIONS}
                      defaultValue={TYPE_OPTIONS[0]?.key as string}
                      errorText={errors?.[LettersOfMedicalNecessityFormField.type]?.message}
                    />
                  ) : (
                    <Box width="100%">
                      <ToggleButtonGroup
                        testId="lmn_type"
                        value={field.value}
                        options={TYPE_OPTIONS}
                        onChange={field.onChange}
                        columns={['auto']}
                      />
                    </Box>
                  )
                }
              />
            </ControllerWrapper>
          </FieldWrapper>
        )}

        <FieldWrapper>
          <Label
            name={LettersOfMedicalNecessityFormField.name}
            isNarrow
            showRequireIcon={mode !== LMNPopupMode.VIEW}
          >
            {t('Name')}
          </Label>

          <ControllerWrapper errorText={errors?.[LettersOfMedicalNecessityFormField.name]?.message}>
            <Controller
              name={LettersOfMedicalNecessityFormField.name}
              control={control}
              render={({ field }) =>
                mode === LMNPopupMode.VIEW ? (
                  <Text>{field.value}</Text>
                ) : (
                  <RegularField
                    testId="lmn_name"
                    name={LettersOfMedicalNecessityFormField.name}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('Name')}
                    isError={!!errors?.[LettersOfMedicalNecessityFormField.name]?.message}
                    editMode={mode === LMNPopupMode.UPLOAD || mode === LMNPopupMode.EDIT}
                  />
                )
              }
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper align="start" fill>
          <Label name={LettersOfMedicalNecessityFormField.description} isNarrow showRequireIcon={false}>
            {t('Description')}
          </Label>
          <ControllerWrapper
            errorText={errors?.[LettersOfMedicalNecessityFormField.description]?.message}
            style={{
              width: '100%',
            }}
          >
            <Controller
              name={LettersOfMedicalNecessityFormField.description}
              control={control}
              render={({ field }) =>
                mode === LMNPopupMode.VIEW ? (
                  <Text>{field.value}</Text>
                ) : (
                  <RegularField
                    testId="lmn_description"
                    name={LettersOfMedicalNecessityFormField.description}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('Description')}
                    inputType={FieldTypes.TextArea}
                    isError={!!errors?.[LettersOfMedicalNecessityFormField.description]?.message}
                    editMode={mode === LMNPopupMode.UPLOAD || mode === LMNPopupMode.EDIT}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                )
              }
            />
          </ControllerWrapper>
        </FieldWrapper>

        {(mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT) && (
          <FieldWrapper align="start">
            <Label name={LettersOfMedicalNecessityFormField.validDates} isNarrow>
              {t('Valid dates')}
            </Label>
            <ControllerWrapper>
              <Controller
                name={LettersOfMedicalNecessityFormField.validDates}
                control={control}
                render={({ field }) => <Text>{field.value || EMPTY_FIELD_VALUE}</Text>}
              />
            </ControllerWrapper>
          </FieldWrapper>
        )}
      </FormFieldsContainer>
    </Box>
  );
};

export default DocumentPreviewPopupForm;
